import { CommonModule } from '@angular/common';
import { Component, Inject, Input, OnChanges, OnDestroy } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { ResolvedFaq } from '../../../admin/blogs/models/resolved-faq.model';
import { StructuredDataService } from '../../../services/structured-data.service';

@Component({
  selector: 'app-faq-section-public',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './faq-section-public.component.html',
  styleUrls: ['./faq-section-public.component.scss']
})
export class FaqSectionPublicComponent implements OnChanges, OnDestroy {
  /** List of FAQs (question + answer HTML) */
  @Input() resolved_items: ResolvedFaq[] = [];

  /** Optional heading shown above the list */
  @Input() heading: string | null = null;
  @Input() headingId = 'faq-heading';

  /** If true, open the first item when no item has is_expanded_by_default=true */
  @Input() openFirstByDefault = true;

  /** Inject structured data for Google (FAQPage) */
  @Input() enableJsonLd = true;
  
  // JSON-LD key shared for FAQ markup
  private readonly schemaKey = 'faqpage';

  constructor(
    @Inject(DOCUMENT) private doc: Document,
    private structuredData: StructuredDataService,
  ) {}

  ngOnChanges(): void {
    if (!this.enableJsonLd) {
      this.structuredData.removeJsonLd(this.schemaKey);
      return;
    }
    this.injectJsonLd();
  }

  ngOnDestroy(): void {
    this.structuredData.removeJsonLd(this.schemaKey);
  }

  get resolvedHeading(): string | null {
    const explicitHeading = (this.heading || '').trim();
    if (explicitHeading) {
      return explicitHeading;
    }

    const lang = (this.doc?.documentElement?.lang || 'en').trim().toLowerCase();
    const localizedHeadings: Record<string, string> = {
      en: 'Frequently Asked Questions',
      de: 'Häufig gestellte Fragen',
      ru: 'Часто задаваемые вопросы',
      tr: 'Sıkça Sorulan Sorular',
    };
    return localizedHeadings[lang] || localizedHeadings['en'];
  }

  /** Whether a given item should be open on first render */
  isOpen(i: number, item: ResolvedFaq): boolean {
    const explicit = this.toBool(item?.is_expanded_by_default);
    if (explicit) return true;

    const anyMarked = (this.resolved_items || []).some(x => this.toBool(x?.is_expanded_by_default));
    if (!anyMarked && this.openFirstByDefault) return i === 0;

    return false;
  }

  /** Stable anchor id without relying on a missing `anchor` field */
  anchorFor(i: number, item: ResolvedFaq): string {
    const fromKey = item?.key?.trim();
    const fromIdent = item?.internal_identifier?.trim();
    const fromId = item?.id != null ? `faq-${item.id}` : '';
    const fromQ = item?.question ? `faq-${this.slugify(item.question)}` : '';
    const anchor = this.slugify(fromKey || fromIdent || fromId || fromQ || `faq-${i + 1}`);
    return this.headingId === 'faq-heading' ? anchor : `${this.headingId}-${anchor}`;
  }

  // ---------- JSON-LD ----------
  private injectJsonLd() {
    const items = (this.resolved_items || [])
      .filter(i => (i?.question || '').trim())
      .map(i => ({
        question: this.stripHtml(i.question || ''),
        answer: this.stripHtml(i.answer || ''),
      }));

    if (!items.length) {
      this.structuredData.removeJsonLd(this.schemaKey);
      return;
    }

    this.structuredData.upsertJsonLd(this.schemaKey, this.structuredData.buildFaqPageData(items));
  }

  // ---------- helpers ----------
  private toBool(v: unknown): boolean {
    if (typeof v === 'boolean') return v;
    if (v == null) return false;
    const s = String(v).toLowerCase().trim();
    return s === '1' || s === 'true' || s === 'yes' || s === 'y';
  }

  private slugify(s: string): string {
    return (s || '')
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .replace(/-{2,}/g, '-');
  }

  private stripHtml(html: string): string {
    const el = this.doc.createElement('div');
    el.innerHTML = html || '';
    return (el.textContent || el.innerText || '').trim();
  }
}
